/**
 * NetSuite PO validation — browser-side evaluator
 * ================================================
 * A pure, dependency-free port of the server's `NetSuiteService.validate_pure`
 * (backend/src/services/netsuite_service.py). It exists so the review screen can
 * validate as the user types without a single network request (issue #291).
 *
 * ## Design rule: this file holds NO business rules
 *
 * Every rule — which fields are mandatory, their labels, the date format, the
 * per-line numeric limits, which keys are non-postable — arrives at runtime in the
 * `validation` bundle from `GET /api/netsuite/lookups?template_id=...`. This module
 * is a generic *evaluator* of that bundle. Keeping the rules on one side is what
 * stops the Python and JavaScript implementations from drifting apart.
 *
 * The only rules hard-coded here are in `NS_BASELINE_BUNDLE`, the degraded
 * fallback used when the bundle cannot be fetched.
 *
 * ## Parity
 *
 * Both implementations are asserted against the shared corpus in
 * `nsValidationCases.json`:
 *   - JS:     nsValidation.test.js                      (vitest)
 *   - Python: scripts/tests/test_ns_validation_parity.py (pytest)
 * Change a rule in the bundle builder and add a case there — never edit one side
 * of this pair alone.
 *
 * The server remains the authoritative gate: `/api/netsuite/validate` still runs
 * before posting and additionally performs the live NetSuite checks (vendor
 * existence, item↔subsidiary) that cannot be answered in the browser.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Fallback rules (AC4)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Degraded rule set for when the bundle fetch fails.
 *
 * Deliberately a strict SUBSET of a real bundle: it mirrors the account-level
 * requirements NetSuite imposes on every PO, and nothing template-specific. A
 * subset can only ever under-report, never invent a blocker on a field the user
 * has no way to satisfy. Template-specific mandatory fields simply go unflagged
 * until the pre-post server validation catches them.
 *
 * Mirrors `_ACCOUNT_REQUIRED_FIELDS` minus `_VENDOR_DERIVED` in netsuite_service.py.
 */
export const NS_BASELINE_BUNDLE = Object.freeze({
  mandatory_keys: ['entity', 'location', 'subsidiary', 'tranDate'],
  field_labels: {
    entity: 'Vendor',
    tranDate: 'Transaction Date',
    subsidiary: 'Subsidiary',
    location: 'Location',
  },
  date_fields: {
    tranDate: 'Transaction Date',
    dueDate: 'Due Date',
    shipDate: 'Ship Date',
  },
  date_format_regex: '^\\d{4}-\\d{2}-\\d{2}$',
  date_format_hint: 'YYYY-MM-DD format (e.g. 2026-01-15)',
  line_rules: { quantity: { min_exclusive: 0 }, rate: { min: 0 } },
  never_post: ['id', 'links', 'status'],
  read_only_computed: ['total', 'subtotal', 'taxTotal'],
  vendor_derived: ['currency', 'terms'],
  reference_fields: [],
  lookup_entity_map: {},
  template_id: null,
  is_baseline: true,
})

const DEFAULT_DATE_REGEX = '^\\d{4}-\\d{2}-\\d{2}$'
const DEFAULT_DATE_HINT = 'YYYY-MM-DD format (e.g. 2026-01-15)'

// ─────────────────────────────────────────────────────────────────────────────
// Value helpers — these must match CPython's semantics, not JavaScript's
// ─────────────────────────────────────────────────────────────────────────────

function isPlainObject(v) {
  // Matches Python's `isinstance(val, dict)`: arrays and null are NOT dicts.
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

/**
 * Whether a submitted field value counts as "no value supplied".
 *
 * Mirrors `_is_blank_value` in netsuite_service.py. A reference field must be
 * judged by its `id`, because a naive emptiness test on the object form lets an
 * unselected reference through: in Python `str({'id': ''})` is the non-empty
 * `"{'id': ''}"`, so the field passes the mandatory check and is then skipped by
 * the live vendor check too, only failing inside NetSuite at post time.
 */
export function isBlankValue(val) {
  if (val === null || val === undefined) return true
  if (isPlainObject(val)) return String(val.id ?? '').trim() === ''
  return String(val).trim() === ''
}

/**
 * Parse a value the way Python's `float()` does.
 *
 * Returns `{ ok: true, value }` when `float()` would succeed, `{ ok: false }`
 * when it would raise (TypeError/ValueError). The distinction drives two
 * different user-facing messages, so plain `Number()` is not good enough:
 *   - `Number('')` is 0 but `float('')` raises
 *   - `Number(null)` is 0 but `float(None)` raises
 *   - `Number('infinity')` is NaN but `float('infinity')` is inf
 *   - `Number('0x10')` is 16 but `float('0x10')` raises
 *   - `Number('nan')` is NaN, and `float('nan')` also yields NaN — which is a
 *     successful parse, and NaN fails every comparison, so no error is reported
 *
 * Not modelled: Python's underscore digit separators (`float('1_000')`). A
 * quantity field carrying that is beyond what either side needs to agree on.
 */
function pyFloat(raw) {
  if (raw === null || raw === undefined) return { ok: false }
  if (typeof raw === 'number') return { ok: true, value: raw }
  if (typeof raw === 'boolean') return { ok: true, value: raw ? 1 : 0 }
  if (typeof raw !== 'string') return { ok: false }

  const s = raw.trim()
  if (s === '') return { ok: false }
  // Python's float() rejects hex/octal/binary literals; JS Number() accepts them.
  if (/^[+-]?0[xXoObB]/.test(s)) return { ok: false }

  const n = Number(s)
  if (Number.isNaN(n)) {
    // Number() yields NaN for both unparseable input and the literal "NaN".
    // Python parses nan/inf/infinity (any case, optional sign) successfully.
    if (/^[+-]?nan$/i.test(s)) return { ok: true, value: NaN }
    if (/^[+-]?(inf|infinity)$/i.test(s)) {
      return { ok: true, value: s.startsWith('-') ? -Infinity : Infinity }
    }
    return { ok: false }
  }
  return { ok: true, value: n }
}

// ─────────────────────────────────────────────────────────────────────────────
// Bundle compilation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Pre-compute the lookups and the RegExp once, so `validateNsPayload` stays
 * allocation-light enough to run inside a render pass on every keystroke.
 *
 * Call this when the bundle arrives (memoised on the bundle identity), NOT on
 * every validation.
 */
export function compileBundle(bundle) {
  const b = bundle && typeof bundle === 'object' ? bundle : NS_BASELINE_BUNDLE

  let dateRe
  try {
    dateRe = new RegExp(b.date_format_regex || DEFAULT_DATE_REGEX)
  } catch {
    // A malformed pattern from the server must not break validation outright.
    dateRe = new RegExp(DEFAULT_DATE_REGEX)
  }

  const lineRules = b.line_rules || NS_BASELINE_BUNDLE.line_rules

  return {
    raw: b,
    isBaseline: !!b.is_baseline,
    rulesVersion: b.rules_version || null,
    mandatoryKeys: Array.isArray(b.mandatory_keys) ? b.mandatory_keys : [],
    labels: b.field_labels || {},
    dateFields: b.date_fields || {},
    dateRe,
    dateHint: b.date_format_hint || DEFAULT_DATE_HINT,
    qtyMinExclusive: lineRules?.quantity?.min_exclusive ?? 0,
    rateMin: lineRules?.rate?.min ?? 0,
    // One Set for the whole payload-cleaning pass.
    stripKeys: new Set([
      ...(b.never_post || []),
      ...(b.read_only_computed || []),
      ...(b.vendor_derived || []),
    ]),
    referenceFields: new Set(b.reference_fields || []),
    lookupEntityMap: b.lookup_entity_map || {},
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// The evaluator
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Run every validation layer that needs neither NetSuite nor the database:
 * L1 mandatory, L2 date format, the per-line numeric checks, and L4 payload
 * cleaning.
 *
 * @param {object} compiled  result of compileBundle()
 * @param {object} payload   header fields keyed by NS field key
 * @param {Array}  lineItems [{ item, quantity, rate, description }]
 * @returns {{ errors: Array<{field_key, message, layer}>, cleanPayload: object }}
 *          Error objects use snake_case keys to match the server response shape
 *          exactly, so the existing error UI needs no changes.
 */
export function validateNsPayload(compiled, payload = {}, lineItems = []) {
  const c = compiled && compiled.mandatoryKeys ? compiled : compileBundle(NS_BASELINE_BUNDLE)
  const errors = []
  const clean = { ...(payload || {}) }
  const lines = Array.isArray(lineItems) ? lineItems : []

  // ── Layer 1: mandatory fields ──────────────────────────────────────────────
  // Bundle order is authoritative (the server sorts it) so error ordering is
  // stable between the two implementations.
  for (const key of c.mandatoryKeys) {
    if (isBlankValue(clean[key])) {
      errors.push({
        field_key: key,
        message: `Please select a value for ${c.labels[key] ?? key}.`,
        layer: 'mandatory',
      })
    }
  }

  // ── Layer 2: date format ───────────────────────────────────────────────────
  for (const key of Object.keys(clean)) {
    const value = clean[key]
    if (value === null || value === undefined || String(value).trim() === '') continue
    const valStr = isPlainObject(value) ? '' : String(value).trim()
    if (key in c.dateFields && valStr) {
      if (!c.dateRe.test(valStr)) {
        errors.push({
          field_key: key,
          message: `${c.dateFields[key]} should be a date in ${c.dateHint}.`,
          layer: 'format',
        })
      }
    }
  }

  // ── Line items ─────────────────────────────────────────────────────────────
  for (let idx = 0; idx < lines.length; idx += 1) {
    const line = lines[idx] || {}
    const lineNum = idx + 1

    const itemRef = line.item
    const itemId = isPlainObject(itemRef) ? itemRef.id : itemRef
    if (!itemRef || !itemId) {
      errors.push({
        field_key: `line_${lineNum}_item`,
        message: `Line ${lineNum}: Please select an item from the list.`,
        layer: 'line_item',
      })
    }

    // Python reads line.get("quantity", 0) — a missing key defaults to 0, which
    // then fails the minimum rather than the is-it-a-number check.
    const qty = pyFloat(line.quantity === undefined ? 0 : line.quantity)
    if (!qty.ok) {
      errors.push({
        field_key: `line_${lineNum}_quantity`,
        message: `Line ${lineNum}: Enter a valid number for quantity.`,
        layer: 'line_item',
      })
    } else if (qty.value <= c.qtyMinExclusive) {
      errors.push({
        field_key: `line_${lineNum}_quantity`,
        message: `Line ${lineNum}: Quantity must be at least 1.`,
        layer: 'line_item',
      })
    }

    // An unparseable rate is tolerated on both sides (NetSuite coerces it);
    // only a genuinely negative number is an error.
    const rate = pyFloat(line.rate === undefined ? 0 : line.rate)
    if (rate.ok && rate.value < c.rateMin) {
      errors.push({
        field_key: `line_${lineNum}_rate`,
        message: `Line ${lineNum}: Rate cannot be negative.`,
        layer: 'line_item',
      })
    }
  }

  // ── Layer 4: payload clean ─────────────────────────────────────────────────
  // Note the asymmetry with L1, which is intentional and matches the server: an
  // empty STRING is stripped, but a reference object with a blank id is kept
  // (L1 has already reported it, so the payload never gets posted).
  for (const key of Object.keys(clean)) {
    const v = clean[key]
    if (c.stripKeys.has(key)) delete clean[key]
    else if (v === null || v === undefined) delete clean[key]
    else if (typeof v === 'string' && v.trim() === '') delete clean[key]
  }

  return { errors, cleanPayload: clean }
}

/**
 * Convenience wrapper: validate straight from a raw bundle.
 * Prefer compileBundle() + validateNsPayload() on hot paths so the bundle is
 * compiled once rather than on every keystroke.
 */
export function validateWithBundle(bundle, payload, lineItems) {
  return validateNsPayload(compileBundle(bundle), payload, lineItems)
}
